import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import type { SelectOption } from "@/lib/options"
import { capitalize, formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconCheck,
  IconDots,
  IconEye,
  IconFileTypePdf,
  IconFileUpload,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from "@tabler/icons-react"
import { useState } from "react"

interface CustomerOption {
  id: number
  name: string
}

interface Invoice {
  id: number
  invoice_number: string
  customer_name: string
  customer_id: number
  invoice_date: string
  due_date: string | null
  date_paid: string | null
  formatted_total: string
  formatted_net_amount: string | null
  currency: string
  status: string
  invoice_pdf_url: string | null
  sent_to_accounting: boolean
  can_update: boolean
  can_destroy: boolean
}

// Keyed by status value, plus "all". The keys come from Invoice::STATUSES, so
// they are not spelled out here.
type StatusCounts = Record<string, number>

interface Props {
  [key: string]: unknown
  invoices: Invoice[]
  pagy: PagyMeta
  status_counts: StatusCounts
  statuses: SelectOption[]
  filters: {
    search?: string
    status?: string
    currency?: string
    customer_id?: string
    start_date?: string
    end_date?: string
  }
  customers: CustomerOption[]
  can_create: boolean
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  paid: "default",
  sent: "secondary",
  draft: "outline",
  overdue: "destructive",
  cancelled: "outline",
}

export default function InvoicesIndex() {
  const { invoices, pagy, status_counts, statuses, filters, customers, can_create } = usePage<Props>().props

  const statusFilters: { label: string; value?: string; count: number }[] = [
    { label: "All", value: undefined, count: status_counts.all ?? 0 },
    ...statuses.map((s) => ({ label: s.label, value: s.value, count: status_counts[s.value] ?? 0 })),
  ]

  const [search, setSearch] = useState(filters.search || "")

  function applyFilters(overrides: Record<string, string | undefined>) {
    const params: Record<string, string | undefined> = {
      search: filters.search,
      status: filters.status,
      currency: filters.currency,
      customer_id: filters.customer_id,
      start_date: filters.start_date,
      end_date: filters.end_date,
      ...overrides,
    }
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))
    router.get("/invoices", cleaned, { preserveState: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({ search: search || undefined })
  }

  function clearFilters() {
    setSearch("")
    router.get("/invoices", {}, { preserveState: true })
  }

  function handleFilter(status?: string) {
    applyFilters({ status })
  }

  function handleDelete(id: number) {
    if (confirm("Are you sure you want to delete this invoice?")) {
      router.delete(`/invoices/${id}`)
    }
  }

  const hasFilters =
    filters.search ||
    filters.status ||
    filters.currency ||
    filters.customer_id ||
    filters.start_date ||
    filters.end_date

  return (
    <>
      <Head title="Invoices - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Invoices</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Invoices</h1>
          {can_create && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" asChild>
                <a href="/invoices/upload">
                  <IconFileUpload className="mr-1 h-4 w-4" />
                  Upload PDF
                </a>
              </Button>
              <Button size="sm" asChild>
                <a href="/invoices/new">
                  <IconPlus className="mr-1 h-4 w-4" />
                  New Invoice
                </a>
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <form onSubmit={handleSearch} className="flex gap-2 lg:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search invoices..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={filters.customer_id || ""}
              onValueChange={(v) => applyFilters({ customer_id: v || undefined })}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="Customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <IconX className="mr-1 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground hidden text-sm sm:inline">Status:</span>
          {statusFilters.map((filter) => (
            <Button
              key={filter.label}
              variant={filters.status === filter.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleFilter(filter.value)}
            >
              {filter.label} ({filter.count})
            </Button>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Invoice Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Date Paid</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Net Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">PDF</TableHead>
              <TableHead className="text-center">Accounting</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell className="font-medium">
                  <a href={`/invoices/${invoice.id}`} className="hover:underline">
                    {invoice.invoice_number}
                  </a>
                </TableCell>
                <TableCell>
                  <a href={`/customers/${invoice.customer_id}`} className="hover:underline">
                    {invoice.customer_name}
                  </a>
                </TableCell>
                <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                <TableCell>{formatDate(invoice.due_date)}</TableCell>
                <TableCell>{formatDate(invoice.date_paid)}</TableCell>
                <TableCell className="text-right font-medium whitespace-nowrap">{invoice.formatted_total}</TableCell>
                <TableCell className="text-right font-medium whitespace-nowrap">
                  {invoice.formatted_net_amount ?? <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[invoice.status] || "outline"}>{capitalize(invoice.status)}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  {invoice.invoice_pdf_url ? (
                    <a
                      href={invoice.invoice_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 inline-flex"
                    >
                      <IconFileTypePdf className="h-4 w-4" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {invoice.sent_to_accounting ? (
                    <IconCheck className="text-green-600 dark:text-green-400 mx-auto h-4 w-4" />
                  ) : (
                    <IconX className="text-muted-foreground mx-auto h-4 w-4" />
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <IconDots className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <a href={`/invoices/${invoice.id}`}>
                          <IconEye className="mr-2 h-4 w-4" />
                          View
                        </a>
                      </DropdownMenuItem>
                      {invoice.can_update && (
                        <DropdownMenuItem asChild>
                          <a href={`/invoices/${invoice.id}/edit`}>
                            <IconPencil className="mr-2 h-4 w-4" />
                            Edit
                          </a>
                        </DropdownMenuItem>
                      )}
                      {invoice.can_destroy && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onClick={() => handleDelete(invoice.id)}>
                            <IconTrash className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-muted-foreground text-center">
                  No invoices found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

InvoicesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
